import { Link } from '@tanstack/react-router';
import {
  Bell,
  CalendarClock,
  CreditCard,
  Gauge,
  Hand,
  Hourglass,
  MessageSquareX,
  Store,
  Timer,
  Unplug,
  UserCheck,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { formatRelative } from '../../dates';
import { t } from '../../i18n';
import { List, ListItem } from '../data/List';
import { EmptyState } from '../feedback/EmptyState';
import { Icon } from '../icons';
import { Button } from '../primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';
import type { DataError, Schemas } from '../types';

type Notification = Schemas['Notification'];

type NotificationBellProps = {
  unreadCount: number;
  notifications: Notification[] | undefined;
  loading?: boolean;
  error?: DataError | null;
  /** Opening is when the app fetches the first page. */
  onOpenChange?: (open: boolean) => void;
  /** The row's link navigates; the app marks the notification read. */
  onOpenNotification: (notification: Notification) => void;
  onMarkAllRead: () => void;
  markingAllRead?: boolean;
  /** The notifications page: /ui knows no route. */
  allHref: string;
};

const typeIcons: Record<Schemas['NotificationType'], LucideIcon> = {
  handoff_created: Hand,
  conversation_assigned: UserCheck,
  waiting_too_long: Hourglass,
  window_closing: Timer,
  message_failed: MessageSquareX,
  channel_problem: Unplug,
  store_problem: Store,
  payment_failed: CreditCard,
  card_expiring: CalendarClock,
  usage_80: Gauge,
  usage_100: Gauge,
};

// The popover lists the ten newest; the notifications page has the rest (merchant screens §3.13).
const newest = 10;

// The unread count and the newest notifications, without leaving the screen (merchant screens §3.13).
export function NotificationBell({
  unreadCount,
  notifications,
  loading,
  error,
  onOpenChange,
  onOpenNotification,
  onMarkAllRead,
  markingAllRead = false,
  allHref,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const content = useRef<HTMLDivElement>(null);
  const label = t('components.NotificationBell.label');

  function change(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  return (
    <Popover open={open} onOpenChange={change}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Icon icon={Bell} />
          <span className="sr-only">{label}</span>
          {unreadCount > 0 && (
            <>
              {/* The count is read once, through the sentence after it. */}
              <span
                aria-hidden
                className="absolute end-1 top-1 rounded-full bg-text px-1 text-xs font-semibold text-background"
              >
                {unreadCount}
              </span>
              <span className="sr-only">
                {t('components.NotificationBell.unread', { count: unreadCount })}
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={content}
        align="end"
        aria-label={label}
        className="flex max-h-(--radix-popover-content-available-height) w-80 flex-col p-0"
        // Radix skips links when it picks the first stop, which would be Tümünü okundu işaretle. The popover itself
        // takes focus instead, so Tab starts at the first row.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          content.current?.focus();
        }}
      >
        <div className="min-h-0 overflow-y-auto">
          <List
            label={label}
            items={notifications?.slice(0, newest)}
            getKey={(notification) => notification.notification_id}
            loading={loading}
            error={
              error && {
                ...error,
                message: error.message ?? t('components.NotificationBell.error'),
              }
            }
            empty={<EmptyState icon={Bell} message={t('components.NotificationBell.empty')} />}
            renderItem={(notification) => (
              // The click on the row's link reaches here too: the app marks the row read, the popover closes.
              <div
                onClick={() => {
                  onOpenNotification(notification);
                  change(false);
                }}
              >
                <ListItem
                  title={notification.title}
                  href={notification.url}
                  unread={notification.read_at == null}
                  leading={<Icon icon={typeIcons[notification.type]} className="text-muted" />}
                  description={notification.body}
                  titleAside={
                    <time dateTime={notification.created_at}>
                      {formatRelative(notification.created_at)}
                    </time>
                  }
                />
              </div>
            )}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-border p-2">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkAllRead}
              disabled={markingAllRead}
              aria-busy={markingAllRead || undefined}
            >
              {t('components.NotificationBell.markAllRead')}
            </Button>
          )}
          <Button variant="link" size="sm" className="ms-auto" asChild>
            <Link to={allHref} onClick={() => change(false)}>
              {t('components.NotificationBell.viewAll')}
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
